/**
 * Mapping des routes vers les permissions requises.
 * - string: Une seule permission requise
 * - string[]: Au moins une des permissions requise (OR)
 * - null: Route réservée aux super-admins uniquement
 * - undefined (route non listée): Pas de permission spécifique requise
 */
export const routePermissions: Record<string, string | string[] | null> = {
    // Dashboard
    '/dashboard': 'dashboard.view',

    // Flows
    '/flows': 'flows.view',
    '/flows/create': 'flows.create',
    '/flows/update': 'flows.edit',
    '/flows/statistics': 'flows.view',
    '/flows/quick-start': 'flows.create',
    '/flows/onboarding': 'flows.create',

    // Segments
    '/segments': 'segments.view',
    '/segments/create': 'segments.create',
    '/segments/update': 'segments.edit',

    // Surveys
    '/surveys': 'surveys.view',
    '/surveys/create': 'surveys.create',
    '/surveys/update': 'surveys.view',  // Mode lecture seule si pas surveys.edit
    '/surveys/analytics': 'surveys.view',

    // Messaging
    '/messaging': 'messaging.view',

    // Cardex (Customers)
    '/cardex': 'customers.view',
    '/companies': 'companies.view',
    '/events': 'events.view',

    // Products (inclut Services et Catalogues)
    '/products': 'products.view',
    '/products/list': 'products.view',
    '/products/list/update': 'products.view',  // Mode lecture seule si pas products.edit
    '/products/list/categories': 'products.view',
    '/products/services': 'products.view',
    '/products/services/list': 'products.view',
    '/products/services/update': 'products.view',  // Mode lecture seule si pas products.edit
    '/products/services/categories': 'products.view',
    '/products/catalogs': 'products.view',
    '/products/catalogs/update': 'products.view',  // Mode lecture seule si pas products.edit
    '/products/orders': 'products.view',
    '/products/statistics': 'products.view',
    '/products/sales-pages': 'products.view',
    // Module Demandes de réservation : sa propre permission, pas products.view
    '/products/booking-requests': 'booking_requests.view',
    '/products/bookable-places': 'booking_requests.view',
    '/products/create': 'products.create',
    '/products/update': 'products.edit',

    // Email Templates
    '/email-templates': 'email_templates.view',
    '/email-templates/create': 'email_templates.create',
    '/email-templates/ai/new': 'email_templates.create',
    '/email-templates/update': 'email_templates.edit',

    // Reviews
    '/reviews': 'reviews.view',
    '/review-widgets': 'reviews.view',

    // Newsletter Widgets : l'API exige customers.view ET customers.edit ; la route
    // n'exprime qu'un droit, customers.view étant déjà requis pour le Cardex.
    '/newsletter-widgets': 'customers.edit',

    // Loyalty
    '/loyalty': 'loyalty.view',

    // Settings - Granulaires
    '/settings': 'settings.view',
    '/settings/facilities': 'facility.view',
    '/settings/identity': 'facility.view',
    '/settings/users': 'users.view',
    '/settings/roles': 'roles.view',
    '/settings/connectivities': 'connectivities.view',
    '/settings/brandings': 'brandings.view',
    '/settings/tags': 'tags.view',
    '/settings/redirect': 'settings.view',
    '/settings/facility-groups': 'facility.view',
    '/settings/mapping-keys': 'settings.view',
    '/settings/email-packs': 'settings.view',
    '/settings/wallet-recharges': 'settings.view',
    '/settings/sms-geo-permissions': 'settings.view',
    '/settings/whatsapp': 'messaging.edit',
    '/settings/smtp': 'settings.view',
    '/settings/ai': 'facility.view',
    '/settings/communications': 'settings.view',
    '/settings/site-chat': 'settings.view',
    '/settings/mailboxes': 'facility.edit',

    // Payments
    '/payments': 'payments.view',
    '/payments/transactions': 'payments.view',
    '/payments/cards': 'payments.view',
    '/payments/links': 'payments.view',
    '/payments/links/edit': 'payments.edit',
    '/payments/config': 'payments.config',

    // Housekeeping — les pages de gestion maintenance / objets trouvés sont réservées
    // aux gestionnaires. Les agents d'entretien (qui n'ont que housekeeping.view/edit)
    // signalent depuis "Mes chambres" mais ne consultent pas ces listes globales.
    // `/housekeeping` couvre par préfixe le tableau, my-rooms, clean/:id, etc. : housekeeping.view,
    // que tous les rôles du ménage ont (agent, gouvernante, technicien).
    '/housekeeping': 'housekeeping.view',
    '/housekeeping/maintenance': 'housekeeping.maintenance.view',
    '/housekeeping/messages': 'housekeeping.view',
    '/housekeeping/lost-found': 'housekeeping.lostfound.view',

    // Changelog
    '/changelog': 'changelog.view',

    // CRM - Accessible aux super-admins + users avec flags CRM
    '/super-admin/crm': '_crm:any',
    '/super-admin/crm/tasks': '_crm:any',
    '/super-admin/crm/prospects': '_crm:prospects',
    // API sous le groupe `crm:prospects` : un compte clients-seul qui taperait
    // l'URL recevrait des 403 qui l'éjectent du back-office.
    '/super-admin/crm/demandes': '_crm:prospects',
    // Prospection à froid et RDV Lemcal : API sous `crm/prospecting`, réservée aux super admins (26/10/2026).
    '/super-admin/crm/prospection': null,
    '/super-admin/crm/salons': '_crm:prospects',
    '/super-admin/crm/rdv-a-rattacher': null,
    '/super-admin/crm/facilities': '_crm:clients',

    // Super Admin - Réservé aux super-admins
    '/super-admin': null,
    '/super-admin/facilities': null,
    '/super-admin/demo': null,
    '/super-admin/config/plugs': null,
    '/super-admin/config/ai': null,
    '/super-admin/config/ai/models': null,
    '/super-admin/config/ai/quality': null,
    '/super-admin/config/pms-connectivity': null,
    '/super-admin/config/saml': null,
    '/super-admin/analytics': null,
    '/super-admin/seo/landing-backlinks': null,
    '/super-admin/flow-templates': null,
    '/super-admin/flow-template-types': null,
    '/super-admin/email-styles': null,
    '/super-admin/ai-reference-emails': null,
    '/super-admin/flow-stats': null,
    '/super-admin/flow-stats/tagging': null,
    '/super-admin/flow-stats/health': null,
    '/super-admin/housekeeping-messaging': null,
    // Le préfixe `/super-admin` la couvrirait déjà, mais toutes les sections
    // sont explicites : la table se lit comme l'inventaire des écrans staff.
    '/super-admin/lead-magnets': null,
    '/super-admin/blog': null,
    '/super-admin/support': null,
    '/super-admin/support/unanswered': null,
    '/super-admin/support/stats': null,
    '/super-admin/support/settings': null,
    '/super-admin/support/models': null,
    '/super-admin/support/manual': null,
    '/super-admin/help-articles': null,
};

/**
 * Ordre de priorité des routes pour la redirection par défaut.
 * La première route accessible sera utilisée.
 */
export const defaultRouteOrder = [
    '/dashboard',
    '/messaging',
    '/cardex',
    '/companies',
    '/events',
    '/flows',
    '/segments',
    '/surveys',
    '/products',
    '/email-templates',
    '/reviews',
    '/housekeeping',
    '/loyalty',
    '/settings',
];

/** Routes de `defaultRouteOrder` liées à un module de l'établissement : module coupé, route non candidate. */
const ROUTE_MODULES: Record<string, 'admin_has_companies' | 'admin_has_events' | 'admin_has_housekeeping'> = {
    '/companies': 'admin_has_companies',
    '/events': 'admin_has_events',
    '/housekeeping': 'admin_has_housekeeping',
};

export type RouteModuleFlags = Partial<Record<'admin_has_companies' | 'admin_has_events' | 'admin_has_housekeeping', boolean | null>>;

/**
 * Première route de `defaultRouteOrder` accessible, ou null. Une route dont le module est coupé n'est
 * pas candidate : une redirection par défaut ne doit jamais atterrir sur un module désactivé.
 * Source unique de SmartRedirect, RequirePermission et PermissionErrorHandler.
 *
 * @param facility drapeaux de modules de l'établissement affiché
 * @param canAccess vérifie une permission (ou une liste, au moins une) de l'utilisateur courant
 */
export function findFirstAccessibleRoute(
    facility: RouteModuleFlags | null | undefined,
    canAccess: (permission: string | string[]) => boolean,
): string | null {
    for (const route of defaultRouteOrder) {
        // Module coupé : la route n'est pas candidate (le garde afficherait « désactivé »).
        const module = ROUTE_MODULES[route];
        if (module && !facility?.[module]) {
            continue;
        }

        const perm = routePermissions[route];

        // Pas de permission requise : route accessible
        if (perm === undefined) {
            return route;
        }

        // Route réservée super-admin : ignorée
        if (perm === null) {
            continue;
        }

        if (canAccess(perm)) {
            return route;
        }
    }

    return null;
}

/**
 * Règles qui ne valent QUE pour le chemin exact, jamais comme préfixe.
 *
 * `routePermissions` sert à la fois de table exacte et de table de préfixes :
 * `'/super-admin': null` y protège aussi toutes les routes staff non listées
 * (`/super-admin/config/roles`, `/super-admin/changelog/…`). Ouvrir le tableau de
 * bord staff aux membres CRM en changeant cette clé aurait donc ouvert tout le
 * back-office. D'où cette table à part, consultée avant l'autre.
 */
export const exactRoutePermissions: Record<string, string | string[] | null> = {
    // Tableau de bord staff : ouvert aux membres CRM, qui n'y voient que leurs sections.
    '/super-admin': '_crm:any',
    '/super-admin/': '_crm:any',
};

/**
 * Pages du compte de l'utilisateur rangées sous un préfixe protégé (`/settings`) : elles ne
 * dépendent d'aucun droit d'établissement, un compte ménage doit pouvoir y gérer sa sécurité.
 * Chemin exact seulement, jamais comme préfixe.
 */
export const ACCOUNT_SECURITY_ROUTE = '/settings/account-security';
const selfServiceRoutes = new Set([ACCOUNT_SECURITY_ROUTE]);

/**
 * Repli des redirections quand aucune route de `defaultRouteOrder` n'est accessible. Jamais
 * `/settings` : sans `settings.view`, la garde y renverrait vers… ce même repli, en boucle.
 * La sécurité du compte est ouverte à tout compte connecté.
 */
export const FALLBACK_ROUTE = ACCOUNT_SECURITY_ROUTE;

/**
 * Trouve la permission requise pour un pathname donné.
 * Cherche d'abord une règle exacte-seulement, puis une correspondance exacte,
 * puis par préfixe.
 */
export function getRequiredPermission(pathname: string): string | string[] | null | undefined {
    // Chemin exact, barre oblique finale tolérée (le routeur l'accepte aussi).
    if (selfServiceRoutes.has(pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname)) {
        return undefined;
    }

    if (exactRoutePermissions[pathname] !== undefined) {
        return exactRoutePermissions[pathname];
    }

    // Correspondance exacte
    if (routePermissions[pathname] !== undefined) {
        return routePermissions[pathname];
    }

    // Chercher par préfixe (du plus spécifique au moins spécifique)
    const sortedRoutes = Object.keys(routePermissions)
        .filter(route => pathname.startsWith(route))
        .sort((a, b) => b.length - a.length);

    if (sortedRoutes.length > 0) {
        return routePermissions[sortedRoutes[0]];
    }

    // Pas de permission requise pour cette route
    return undefined;
}
